import { CommonModule } from "@angular/common";
import { Component, inject, Inject, ViewChild } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/state";
import * as AutoArchiveActions from "src/app/state/auto-archive/auto-archive.actions";
import { FormsModule, NgForm } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { AutoArchiveRule } from "src/app/model/auto-archive/AutoArchiveRule";
import { NotificationService } from "src/app/service/notification.service";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import {
    convertToRegex,
    autoArchivePatternTypes,
} from "src/app/lib/auto-archive";
import { AutoArchiveRuleCondition } from "src/app/model/auto-archive/AutoArchiveRuleCondition";

@Component({
    selector: "app-auto-archive-dialog",
    templateUrl: "./auto-archive-dialog.component.html",
    styleUrls: ["./auto-archive-dialog.component.scss"],
    imports: [
        MatInputModule,
        FormsModule,
        MatButtonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatIconModule,
        MatFormFieldModule,
        MatSelectModule,
        CommonModule,
    ],
    standalone: true,
})
export class RuleDialog {
    constructor(
        private notificationService: NotificationService,
        private store: Store<AppState>,
        @Inject(MAT_DIALOG_DATA) public data: AutoArchiveRule | null
    ) {
        if (data) {
            this.id = data.id ?? null;
            this.endpoint = data.endpoint ?? "";
            this.method = data.method ?? "";
            this.sendingSystem = data.sendingSystem ?? "";
            this.sendingUnit = data.sendingUnit ?? "";
            this.conditions = data.conditions ?? [];
        }
    }

    readonly dialogRef = inject(MatDialogRef<RuleDialog, AutoArchiveRule>);

    @ViewChild("ruleForm") ruleForm?: NgForm;

    id: number | null = null;
    endpoint: string = "";
    method: string = "";
    sendingSystem: string = "";
    sendingUnit: string = "";
    conditions: AutoArchiveRuleCondition[] = [];
    patternTypes = autoArchivePatternTypes;

    onSave(): void {
        this.ruleForm?.form.markAllAsTouched();

        if (!this.endpoint || !this.method || !this.sendingSystem) {
            this.notificationService.showError(
                "Please fill in all required fields"
            );
            return;
        }

        const hasEmptyConditionFields = this.conditions.some(
            (c) =>
                !c.property ||
                !c.displayRegexPatternType ||
                !c.displayRegexValue
        );

        if (hasEmptyConditionFields) {
            this.notificationService.showError(
                "All condition fields must be filled in"
            );
            return;
        }

        const result: AutoArchiveRule = {
            id: this.id ?? undefined,
            endpoint: this.endpoint,
            method: this.method,
            sendingSystem: this.sendingSystem,
            sendingUnit: this.sendingUnit,
            conditions: this.conditions.map((c) => ({
                ...c,
                regex: convertToRegex(
                    c.displayRegexPatternType,
                    c.displayRegexValue
                ),
            })),
        };

        if (this.id) {
            this.store.dispatch(
                AutoArchiveActions.updateRule({ id: this.id, rule: result })
            );
        } else {
            this.store.dispatch(
                AutoArchiveActions.createRule({ rule: result })
            );
        }

        this.dialogRef.close(result);
    }

    removeCondition(index: number): void {
        this.conditions.splice(index, 1);
    }

    addCondition(): void {
        this.conditions.push({
            property: "",
            regex: "",
            displayRegexValue: "",
            displayRegexPatternType: "contains",
            errorCode: "",
        });
    }
}
